import React from 'react'

import { DownloadIcon, CopyIcon } from 'lucide-react'

import { Box, Grid, Stack, Typography, useTheme } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'
import { useSnackbar } from 'contexts'

interface RecoveryCodesModalProps {
  codes: string[] | null
  open: boolean
  onClose: () => void
}

/**
 * VRG-490 §3 — recovery codes are returned by the API EXACTLY ONCE: on
 * first-factor confirm, or on regenerate. There is no endpoint to read them
 * back (the API only ever stores hashes), so this modal is the only place
 * they are ever shown, and closing it is a one-way door — the caller must
 * not attempt to reopen it with the same codes later.
 */
export const RecoveryCodesModal = ({ codes, open, onClose }: RecoveryCodesModalProps) => {
  const theme = useTheme()
  const { open: notification } = useSnackbar()

  const handleCopyAll = async () => {
    if (!codes) return
    try {
      await navigator.clipboard.writeText(codes.join('\n'))
      notification({ type: 'success', title: 'Copied', text: 'Recovery codes copied to your clipboard.' })
    } catch {
      notification({ type: 'error', title: 'Could not copy', text: 'Copy the codes manually instead.' })
    }
  }

  const handleDownload = () => {
    if (!codes) return
    const blob = new Blob([codes.join('\n') + '\n'], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'vera-recovery-codes.txt'
    document.body.appendChild(anchor)
    anchor.click()
    anchor.remove()
    URL.revokeObjectURL(url)
  }

  return (
    <Modal
      title="Save your recovery codes"
      open={open}
      onClose={onClose}
      actions={
        <Button disableElevation variant="primary" onClick={onClose}>
          Done, I've saved these
        </Button>
      }
    >
      <Stack spacing={4}>
        <Alert
          severity="warning"
          description="These codes are shown once and can't be retrieved again, only replaced with a new set. Each one can be used a single time to sign in if you lose access to your other two-factor methods. Store them somewhere safe."
        />

        <Box
          sx={{
            border: `1px solid ${theme.palette.primary[100]}`,
            borderRadius: 2,
            p: 4,
            bgcolor: theme.palette.background.default,
          }}
        >
          <Grid container spacing={2}>
            {(codes ?? []).map(code => (
              <Grid item xs={6} key={code}>
                <Typography
                  variant="body3"
                  sx={{ fontFamily: 'monospace', fontSize: 14, letterSpacing: 0.5 }}
                >
                  {code}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Stack direction="row" spacing={3}>
          <Button disableElevation variant="secondary" startIcon={<CopyIcon strokeWidth={1.5} size={16} />} onClick={handleCopyAll}>
            Copy all
          </Button>
          <Button disableElevation variant="secondary" startIcon={<DownloadIcon strokeWidth={1.5} size={16} />} onClick={handleDownload}>
            Download
          </Button>
        </Stack>
      </Stack>
    </Modal>
  )
}
